import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import {
  IMAGE_CROPPING_GLOBAL_CONFIG,
  ImageCroppingConfig,
} from './image-cropping.config';

export interface DrawableElement {
  id: string;
  x: number;
  y: number;

  shapes: Array<{
    type: string;
    relativeX: number;
    relativeY: number;
    w: number;
    h: number;
  }>;
  xBounded: string;
  yBounded: string;
}

@Component({
  selector: 'ui-image-cropping',
  templateUrl: './image-cropping.component.html',
  styleUrls: ['./image-cropping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageCroppingComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() public height = '100%';
  @Input() public width = '100%';
  @Input() public croppedWidthPX: number | null = null;
  @Input() public croppedHeightPX: number | null = null;
  @Output() public imageCropped: EventEmitter<string> =
    new EventEmitter<string>();
  @ViewChild('canvasElement', { static: false }) private _canvas:
    | ElementRef
    | undefined;

  private _destroy$ = new Subject<void>();
  private _croppingToolsBaseSize = 16;
  private _toolsColor = '#00aeef';
  private _selectedToolColor = '#0789bb';
  private _backdropColor = 'rgba(0, 0, 0, 0.2)';
  private _cropToolDimensions = { width: 0, height: 0 };
  private _previousTouchPosition: { x: number; y: number } | null = null;

  private _canvasWidth = 0;
  private _canvasHeight = 0;

  private _canvasSquare = new Path2D();
  private _framePath = new Path2D();
  private _corners: Record<string, DrawableElement> = {};
  private _movableElements: Record<string, DrawableElement> = {};
  private _movableElementPaths: Array<Path2D> = [];

  private _canvasScale = 1;
  private _imagePosition = { x: 0, y: 0 };

  private _indexOfDraggedCornerElement = -1;
  private _imageIsDragged = false;

  public image = new Image();
  public imageRotation = 0;
  public imageScale = 1;
  public imageScaleStep = 0.01;
  public imageScaleMax = 2;
  public imageScaleMin = 0.5;
  public editingEnabled = true;
  public canvasWidth100Percent = false;

  constructor(
    @Optional()
    @Inject(IMAGE_CROPPING_GLOBAL_CONFIG)
    private _globalConfig: ImageCroppingConfig | null,
    private _cdRef: ChangeDetectorRef,
  ) {}

  public get canvas(): HTMLCanvasElement {
    return this._canvas?.nativeElement;
  }

  public get ctx(): CanvasRenderingContext2D | null {
    return this.canvas.getContext('2d');
  }

  private get _pointsInLineBetweenCorners(): Record<string, DrawableElement> {
    if (!this._corners || Object.values(this._corners).length === 0) {
      return {};
    }

    const {
      'top-left': topLeftCorner,
      'top-right': topRightCorner,
      'bottom-right': bottomRightCorner,
      'bottom-left': bottomLeftCorner,
    } = this._corners;

    if (
      !topLeftCorner ||
      !topRightCorner ||
      !bottomRightCorner ||
      !bottomLeftCorner
    ) {
      return {};
    }
    return {
      top: {
        id: 'top',
        x: topLeftCorner.x + (topRightCorner.x - topLeftCorner.x) / 2,
        y: topLeftCorner.y,
        xBounded: '',
        yBounded: 'y1',
        shapes: [
          {
            type: 'rect',
            relativeX: -8 * this._canvasScale,
            relativeY: -4 * this._canvasScale,
            w: this._croppingToolsBaseSize * this._canvasScale,
            h: (this._croppingToolsBaseSize / 2) * this._canvasScale,
          },
        ],
      },
      right: {
        id: 'right',
        x: topRightCorner.x,
        y: topRightCorner.y + (bottomRightCorner.y - topRightCorner.y) / 2,
        xBounded: 'x2',
        yBounded: '',
        shapes: [
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 2) * this._canvasScale,
            w: (this._croppingToolsBaseSize / 2) * this._canvasScale,
            h: this._croppingToolsBaseSize * this._canvasScale,
          },
        ],
      },
      bottom: {
        id: 'bottom',
        x: bottomRightCorner.x - (bottomRightCorner.x - bottomLeftCorner.x) / 2,
        y: bottomRightCorner.y,
        xBounded: '',
        yBounded: 'y2',
        shapes: [
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 2) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: this._croppingToolsBaseSize * this._canvasScale,
            h: (this._croppingToolsBaseSize / 2) * this._canvasScale,
          },
        ],
      },
      left: {
        id: 'left',
        x: topLeftCorner.x,
        y: bottomRightCorner.y - (bottomLeftCorner.y - topLeftCorner.y) / 2,
        xBounded: 'x1',
        yBounded: '',
        shapes: [
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 2) * this._canvasScale,
            w: (this._croppingToolsBaseSize / 2) * this._canvasScale,
            h: this._croppingToolsBaseSize * this._canvasScale,
          },
        ],
      },
    };
  }

  @Input()
  public set imageToCrop(image: string) {
    this.image.src = image;
    this.initImage();
  }

  public ngOnInit(): void {
    if (this._globalConfig) {
      this.croppedWidthPX = this._globalConfig.croppedWidthPX;
      this.croppedHeightPX = this._globalConfig.croppedHeightPX;
      if (this._globalConfig.toolsColor) {
        this._toolsColor = this._globalConfig.toolsColor;
      }

      if (this._globalConfig.backdropColor) {
        this._backdropColor = this._globalConfig.backdropColor;
      }
    }
  }

  public ngAfterViewInit(): void {
    fromEvent(this.canvas, 'mousedown')
      .pipe(takeUntil(this._destroy$))
      .subscribe((e: Event) => this._dragStart(e));
    fromEvent(this.canvas, 'touchstart')
      .pipe(takeUntil(this._destroy$))
      .subscribe((e: Event) => this._dragStart(e));
    fromEvent(window, 'mousemove')
      .pipe(takeUntil(this._destroy$))
      .subscribe((e: Event) => this._move(e));
    fromEvent(window, 'touchmove')
      .pipe(takeUntil(this._destroy$))
      .subscribe((e: Event) => this._move(e));
    fromEvent(this.canvas, 'wheel')
      .pipe(takeUntil(this._destroy$))
      .subscribe((e: Event) => this._mouseWheel(e));
    fromEvent(window, 'mouseup')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._dragEnd());
    fromEvent(window, 'touchend')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._dragEnd());
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public initCanvasSize(): void {
    const parent = this._canvas?.nativeElement.parentNode;
    if (parent) {
      const frameHeight = this.croppedHeightPX || 0;
      const offsetCanvasFromCropFrame = frameHeight * 0.2;
      const ratio = parent.offsetWidth / parent.offsetHeight;
      this._canvasWidth = (frameHeight + offsetCanvasFromCropFrame) * ratio;
      this._canvasHeight = frameHeight + offsetCanvasFromCropFrame;
      this.canvas.width = this._canvasWidth;
      this.canvas.height = this._canvasHeight;
    }
  }

  public initImage(): void {
    this.image.onload = () => {
      this.initCanvasSize();
      this.image.crossOrigin = 'anonymous';
      this._calculateImageScaleToFillHorizontallyAndVerticallyCanvas();
      this._calculateIfImagePositionIsOutOfBounds();
      this.canvasWidth100Percent = true;

      this._imagePosition = {
        x: this.canvas.width / 2,
        y: this.canvas.height / 2,
      };
      this._drawImage();
      this._prepareCorners();
      this._movableElements = {
        ...this._corners,
        ...this._pointsInLineBetweenCorners,
      };
      this._drawCroppingTools();
    };
  }

  public cropImage(): void {
    if (!this.editingEnabled) {
      return;
    }

    if (!this.ctx) {
      return;
    }

    this._drawImage();

    const {
      'top-left': topLeftCorner,
      'top-right': topRightCorner,
      'bottom-left': bottomLeftCorner,
    } = this._movableElements;

    if (!topLeftCorner || !topRightCorner || !bottomLeftCorner) {
      return;
    }

    const width = topRightCorner.x - topLeftCorner.x;
    const height = bottomLeftCorner.y - topLeftCorner.y;
    const x = topLeftCorner.x;
    const y = topLeftCorner.y;
    const imageData = this.ctx.getImageData(x, y, width, height);
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx.putImageData(imageData, 0, 0);
    this.editingEnabled = false;
    this._cdRef.detectChanges();
    this.imageCropped.emit(this.canvas.toDataURL());
  }

  public fileChangeEvent(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (!e.target) {
          return;
        }
        this.image.src = e.target.result as string;
        this.initImage();
      };
      reader.readAsDataURL(target.files[0]);
    }
  }

  public downloadImage(): void {
    const link = document.createElement('a');
    link.download = 'image.png';
    link.href = this.canvas.toDataURL();
    link.click();
  }

  public restoreImageSize(): void {
    this.canvas.width = this._canvasWidth;
    this.canvas.height = this._canvasHeight;
    this._drawImage();
    this._drawCroppingTools();
    this.editingEnabled = true;
    this._cdRef.detectChanges();
  }

  public changeImageScale(scale: number): void {
    this.imageScale = Math.round(scale * 100) / 100;
    if (this.imageScale > this.imageScaleMax) {
      this.imageScale = this.imageScaleMax;
    }
    if (this.imageScale < this.imageScaleMin) {
      this.imageScale = this.imageScaleMin;
    }
    this._calculateIfImagePositionIsOutOfBounds();
    this._drawImage();
    this._drawCroppingTools();
    this._cdRef.detectChanges();
  }

  public rotateMainImage(): void {
    if (!this.editingEnabled) {
      return;
    }
    this.imageRotation += 90;
    if (this.imageRotation === 360) {
      this.imageRotation = 0;
    }
    this._calculateIfImagePositionIsOutOfBounds();
    this._drawImage();
    this._drawCroppingTools();
  }

  private _drawCroppingTools(): void {
    this._movableElementPaths = [];
    const movableElementsArr = Object.values(this._movableElements);
    if (movableElementsArr.length === 0) {
      return;
    }
    this._drawFrameBorderLines();
    this._drawBackdrops();

    movableElementsArr.forEach((corner, index) => {
      if (!this.ctx) {
        return;
      }
      const path = new Path2D();
      this._movableElementPaths.push(path);
      if (this._indexOfDraggedCornerElement === index) {
        this.ctx.fillStyle = this._selectedToolColor;
      } else {
        this.ctx.fillStyle = this._toolsColor;
      }
      corner.shapes.forEach((shape) => {
        path.rect(
          corner.x + shape.relativeX,
          corner.y + shape.relativeY,
          shape.w,
          shape.h,
        );
      });

      this.ctx.fill(path);
    });
  }

  private _drawImage(): void {
    if (!this.ctx) {
      return;
    }
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.save();
    this.ctx.translate(this._imagePosition.x, this._imagePosition.y);
    this.ctx.scale(this.imageScale, this.imageScale);
    this.ctx.rotate((this.imageRotation * Math.PI) / 180);
    this.ctx.translate(-this.image.width / 2, -this.image.height / 2);
    this.ctx.drawImage(this.image, 0, 0);
    this.ctx.restore();
    this._canvasSquare = new Path2D();
    this._canvasSquare.rect(0, 0, this.canvas.width, this.canvas.height);
  }

  private _drawFrameBorderLines(): void {
    if (!this.ctx) {
      return;
    }
    const {
      'top-left': topLeftCorner,
      'top-right': topRightCorner,
      'bottom-right': bottomRightCorner,
      'bottom-left': bottomLeftCorner,
    } = this._movableElements;

    if (
      !topLeftCorner ||
      !topRightCorner ||
      !bottomRightCorner ||
      !bottomLeftCorner
    ) {
      return;
    }

    this.ctx.strokeStyle = this._toolsColor;
    this.ctx.lineWidth = 2 * this._canvasScale;
    this.ctx.beginPath();
    this.ctx.moveTo(topLeftCorner.x, topLeftCorner.y);
    this.ctx.lineTo(topRightCorner.x, topRightCorner.y);
    this.ctx.lineTo(bottomRightCorner.x, bottomRightCorner.y);
    this.ctx.lineTo(bottomLeftCorner.x, bottomLeftCorner.y);
    this.ctx.closePath();
    this.ctx.stroke();
  }

  private _drawGridLines(): void {
    if (!this.ctx) {
      return;
    }

    const {
      'top-left': topLeftCorner,
      'top-right': topRightCorner,
      'bottom-left': bottomLeftCorner,
    } = this._movableElements;

    if (!topLeftCorner || !topRightCorner || !bottomLeftCorner) {
      return;
    }

    const thirdPartOfWidth = (topRightCorner.x - topLeftCorner.x) / 3;
    const thirdPartOfHeight = (bottomLeftCorner.y - topLeftCorner.y) / 3;

    this.ctx.strokeStyle = '#ddd5cf';
    this.ctx.lineWidth = this._canvasScale;
    this.ctx.beginPath();
    this.ctx.moveTo(topLeftCorner.x + thirdPartOfWidth, topLeftCorner.y);
    this.ctx.lineTo(topLeftCorner.x + thirdPartOfWidth, bottomLeftCorner.y);
    this.ctx.moveTo(topLeftCorner.x + thirdPartOfWidth * 2, topLeftCorner.y);
    this.ctx.lineTo(topLeftCorner.x + thirdPartOfWidth * 2, bottomLeftCorner.y);
    this.ctx.moveTo(topLeftCorner.x, topLeftCorner.y + thirdPartOfHeight);
    this.ctx.lineTo(topRightCorner.x, topLeftCorner.y + thirdPartOfHeight);
    this.ctx.moveTo(topLeftCorner.x, topLeftCorner.y + thirdPartOfHeight * 2);
    this.ctx.lineTo(topRightCorner.x, topLeftCorner.y + thirdPartOfHeight * 2);
    this.ctx.stroke();
  }

  private _dragStart(e: Event): void {
    if (!this.ctx) {
      return;
    }

    if (!(e instanceof MouseEvent || e instanceof TouchEvent)) {
      return;
    }
    e.stopPropagation();
    if (e.cancelable) {
      e.preventDefault();
    }
    let mouseX = 0;
    let mouseY = 0;
    if (window.TouchEvent && e instanceof TouchEvent) {
      const rect = this.canvas.getBoundingClientRect();
      mouseX = e.touches[0].pageX - rect.left;
      mouseY = e.touches[0].clientY - rect.top;
      this._previousTouchPosition = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
    if (e instanceof MouseEvent) {
      mouseX = e.offsetX;
      mouseY = e.offsetY;
    }

    this._indexOfDraggedCornerElement = this._movableElementPaths.findIndex(
      (path) =>
        this.ctx?.isPointInPath(
          path,
          mouseX * this._canvasScale,
          mouseY * this._canvasScale,
        ),
    );
    if (this._indexOfDraggedCornerElement === -1) {
      this._imageIsDragged =
        this.ctx.isPointInPath(
          this._canvasSquare,
          mouseX * this._canvasScale,
          mouseY * this._canvasScale,
        ) || false;
    }

    if (this._indexOfDraggedCornerElement > -1) {
      this.canvas.style.cursor = 'grabbing';
    }
  }

  private _move(e: Event): void {
    if (!(e instanceof MouseEvent || e instanceof TouchEvent)) {
      return;
    }

    if (!this.editingEnabled) {
      return;
    }

    const movableElementsArr = Object.values(this._movableElements);

    if (!this.ctx) {
      return;
    }

    let mouseY = 0;
    let mouseX = 0;
    let movementX = 0;
    let movementY = 0;

    if (window.TouchEvent && e instanceof TouchEvent) {
      mouseX = e.touches[0].clientX;
      mouseY = e.touches[0].clientY;
      if (this._previousTouchPosition) {
        movementX = e.touches[0].clientX - this._previousTouchPosition.x;
        movementY = e.touches[0].clientY - this._previousTouchPosition.y;
        this._previousTouchPosition = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      }
    }

    if (e instanceof MouseEvent) {
      if (!this.editingEnabled) {
        return;
      }
      mouseX = e.offsetX * this._canvasScale;
      mouseY = e.offsetY * this._canvasScale;
      movementX = e.movementX * this._canvasScale;
      movementY = e.movementY * this._canvasScale;
    }

    this._updateCenterLinesPosition();

    this.canvas.style.cursor = 'default';
    movableElementsArr.forEach((movableElement, index) => {
      if (
        this.ctx?.isPointInPath(
          this._movableElementPaths[index],
          mouseX,
          mouseY,
        )
      ) {
        this.canvas.style.cursor = 'grab';
      }
    });
    if (this.ctx?.isPointInPath(this._framePath, mouseX, mouseY)) {
      this.canvas.style.cursor = 'grab';
    }

    if (
      this._indexOfDraggedCornerElement > -1 &&
      !this.croppedWidthPX &&
      !this.croppedHeightPX
    ) {
      this.canvas.style.cursor = 'grabbing';
      this._drawImage();
      const elementToDrag = Object.values(this._movableElements)[
        this._indexOfDraggedCornerElement
      ];
      elementToDrag.x += movementX;
      elementToDrag.y += movementY;

      this._preventElementFromBeingDragOutsideCanvas(elementToDrag);
      this._preventFrameFromFlipping(elementToDrag);
      movableElementsArr.forEach((movableElement) => {
        if (elementToDrag.xBounded === movableElement.xBounded) {
          movableElement.x = elementToDrag.x;
        }
        if (elementToDrag.yBounded === movableElement.yBounded) {
          movableElement.y = elementToDrag.y;
        }
      });
      this._drawCroppingTools();
      this._drawGridLines();
    }
    if (this._imageIsDragged) {
      this.canvas.style.cursor = 'grabbing';
      this._imagePosition.x += movementX;
      this._imagePosition.y += movementY;
      this._calculateIfImagePositionIsOutOfBounds();

      this._drawImage();
      this._drawCroppingTools();
    }

    if (
      this._indexOfDraggedCornerElement > -1 &&
      this.croppedWidthPX &&
      this.croppedHeightPX
    ) {
      this.canvas.style.cursor = 'grabbing';
      this._corners['top-left'].x += movementX;

      this._corners['top-left'].y += movementY;
      this._corners['top-right'].x += movementX;
      this._corners['top-right'].y += movementY;
      this._corners['bottom-left'].x += movementX;
      this._corners['bottom-left'].y += movementY;
      this._corners['bottom-right'].x += movementX;
      this._corners['bottom-right'].y += movementY;

      if (this._corners['top-left'].x < 0) {
        this._corners['top-left'].x = 0;
        this._corners['top-right'].x =
          this._corners['top-left'].x + this._cropToolDimensions.width;
        this._corners['bottom-left'].x = this._corners['top-left'].x;
        this._corners['bottom-right'].x = this._corners['top-right'].x;
      }
      if (
        this._corners['top-left'].x >
        this.canvas.width - this._cropToolDimensions.width
      ) {
        this._corners['top-left'].x =
          this.canvas.width - this._cropToolDimensions.width;
        this._corners['top-right'].x =
          this._corners['top-left'].x + this._cropToolDimensions.width;
        this._corners['bottom-left'].x = this._corners['top-left'].x;
        this._corners['bottom-right'].x = this._corners['top-right'].x;
      }
      if (this._corners['top-left'].y < 0) {
        this._corners['top-left'].y = 0;
        this._corners['top-right'].y = this._corners['top-left'].y;
        this._corners['bottom-left'].y =
          this._corners['top-left'].y + this._cropToolDimensions.height;
        this._corners['bottom-right'].y = this._corners['bottom-left'].y;
      }
      if (
        this._corners['top-left'].y >
        this.canvas.height - this._cropToolDimensions.height
      ) {
        this._corners['top-left'].y =
          this.canvas.height - this._cropToolDimensions.height;
        this._corners['top-right'].y = this._corners['top-left'].y;
        this._corners['bottom-left'].y =
          this._corners['top-left'].y + this._cropToolDimensions.height;
        this._corners['bottom-right'].y = this._corners['bottom-left'].y;
      }
      this._movableElements['top'].y = this._movableElements['top-left'].y;
      this._movableElements['right'].x = this._movableElements['top-right'].x;
      this._movableElements['bottom'].y =
        this._movableElements['bottom-left'].y;
      this._movableElements['left'].x = this._movableElements['top-left'].x;
      this._framePath = new Path2D();
      this._framePath.rect(
        this._corners['top-left'].x,
        this._corners['top-left'].y,
        this._corners['top-right'].x - this._corners['top-left'].x,
        this._corners['bottom-left'].y - this._corners['top-left'].y,
      );
      this._drawImage();
      this._drawCroppingTools();
      this._drawGridLines();
    }
  }

  private _preventElementFromBeingDragOutsideCanvas(
    elementToDrag: DrawableElement,
  ): void {
    if (elementToDrag.x > this.canvas.width) {
      elementToDrag.x = this.canvas.width;
    }

    if (elementToDrag.x < 0) {
      elementToDrag.x = 0;
    }

    if (elementToDrag.y > this.canvas.height) {
      elementToDrag.y = this.canvas.height;
    }

    if (elementToDrag.y < 0) {
      elementToDrag.y = 0;
    }
  }

  private _preventFrameFromFlipping(elementToDrag: DrawableElement): void {
    const {
      'top-left': topLeftCorner,
      'top-right': topRightCorner,
      'bottom-right': bottomRightCorner,
      'bottom-left': bottomLeftCorner,
    } = this._movableElements;
    if (
      !topLeftCorner ||
      !topRightCorner ||
      !bottomRightCorner ||
      !bottomLeftCorner
    ) {
      return;
    }
    if (elementToDrag.id === 'top-left') {
      if (elementToDrag.x > topRightCorner.x) {
        elementToDrag.x = topRightCorner.x;
      }
      if (elementToDrag.y > bottomLeftCorner.y) {
        elementToDrag.y = bottomLeftCorner.y;
      }
    }

    if (elementToDrag.id === 'top-right') {
      if (elementToDrag.x < topLeftCorner.x) {
        elementToDrag.x = topLeftCorner.x;
      }
      if (elementToDrag.y > bottomRightCorner.y) {
        elementToDrag.y = bottomRightCorner.y;
      }
    }

    if (elementToDrag.id === 'bottom-right') {
      if (elementToDrag.x < bottomLeftCorner.x) {
        elementToDrag.x = bottomLeftCorner.x;
      }
      if (elementToDrag.y < topRightCorner.y) {
        elementToDrag.y = topRightCorner.y;
      }
    }

    if (elementToDrag.id === 'bottom-left') {
      if (elementToDrag.x > bottomRightCorner.x) {
        elementToDrag.x = bottomRightCorner.x;
      }
      if (elementToDrag.y < topLeftCorner.y) {
        elementToDrag.y = topLeftCorner.y;
      }
    }

    if (elementToDrag.id === 'top') {
      if (elementToDrag.y > bottomLeftCorner.y) {
        elementToDrag.y = bottomLeftCorner.y;
      }
    }

    if (elementToDrag.id === 'right') {
      if (elementToDrag.x < topLeftCorner.x) {
        elementToDrag.x = topLeftCorner.x;
      }
    }

    if (elementToDrag.id === 'bottom') {
      if (elementToDrag.y < topRightCorner.y) {
        elementToDrag.y = topRightCorner.y;
      }
    }

    if (elementToDrag.id === 'left') {
      if (elementToDrag.x > bottomRightCorner.x) {
        elementToDrag.x = bottomRightCorner.x;
      }
    }
  }

  private _updateCenterLinesPosition(): void {
    const {
      top,
      right,
      bottom,
      left,
      'top-left': topLeftCorner,
      'top-right': topRightCorner,
      'bottom-right': bottomRightCorner,
      'bottom-left': bottomLeftCorner,
    } = this._movableElements;

    if (
      top &&
      right &&
      bottom &&
      left &&
      topLeftCorner &&
      topRightCorner &&
      bottomRightCorner &&
      bottomLeftCorner
    ) {
      top.x = topLeftCorner.x + (topRightCorner.x - topLeftCorner.x) / 2;
      right.y = topRightCorner.y + (bottomRightCorner.y - topRightCorner.y) / 2;
      bottom.x =
        bottomRightCorner.x - (bottomRightCorner.x - bottomLeftCorner.x) / 2;
      left.y = bottomRightCorner.y - (bottomLeftCorner.y - topLeftCorner.y) / 2;
    }
  }

  private _calculateIfImagePositionIsOutOfBounds(): void {
    const rotatedDimensions = this._calculateRotatedDimensions(
      this.image.width * this.imageScale,
      this.image.height * this.imageScale,
      this.imageRotation,
    );
    const boundaryOffset = 20;

    if (
      -1 *
        (rotatedDimensions.height / 2 -
          this._canvasHeight +
          this._imagePosition.y) >=
      this._canvasHeight - boundaryOffset
    ) {
      this._imagePosition.y = -rotatedDimensions.height / 2 + boundaryOffset;
    }

    if (
      rotatedDimensions.height / 2 -
        this._canvasHeight +
        this._imagePosition.y >=
      rotatedDimensions.height - boundaryOffset
    ) {
      this._imagePosition.y =
        this._canvasHeight + rotatedDimensions.height / 2 - boundaryOffset;
    }

    if (
      -1 *
        (rotatedDimensions.width / 2 -
          this._canvasWidth +
          this._imagePosition.x) >=
      this._canvasWidth - boundaryOffset
    ) {
      this._imagePosition.x = -rotatedDimensions.width / 2 + boundaryOffset;
    }

    if (
      rotatedDimensions.width / 2 - this._canvasWidth + this._imagePosition.x >=
      rotatedDimensions.width - boundaryOffset
    ) {
      this._imagePosition.x =
        this._canvasWidth + rotatedDimensions.width / 2 - boundaryOffset;
    }
  }

  private _calculateRotatedDimensions(
    originalWidth: number,
    originalHeight: number,
    angleInDegrees: number,
  ): {
    width: number;
    height: number;
  } {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;

    const sin = Math.abs(Math.sin(angleInRadians));
    const cos = Math.abs(Math.cos(angleInRadians));

    const rotatedWidth = originalWidth * cos + originalHeight * sin;
    const rotatedHeight = originalHeight * cos + originalWidth * sin;

    return { width: rotatedWidth, height: rotatedHeight };
  }

  private _dragEnd(): void {
    if (this._indexOfDraggedCornerElement !== -1) {
      this._indexOfDraggedCornerElement = -1;
      this._drawImage();
      this._drawCroppingTools();
      this.canvas.style.cursor = 'default';
    } else {
      if (this._imageIsDragged) {
        this._imageIsDragged = false;
      }
    }
  }

  private _mouseWheel(e: Event): void {
    if (!(e instanceof WheelEvent)) {
      return;
    }

    if (!this.editingEnabled) {
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }

    e.stopPropagation();
    if (e.deltaY < 0) {
      this.changeImageScale(
        this._roundToStepValue((this.imageScaleMax - this.imageScaleMin) / 5) +
          this.imageScale,
      );
    } else if (e.deltaY > 0) {
      this.changeImageScale(
        this.imageScale -
          this._roundToStepValue(
            (this.imageScaleMax - this.imageScaleMin) / 10,
          ),
      );
    }
  }

  private _prepareCorners(): void {
    this._calculateCanvasResizeScale();
    this._cropToolDimensions = {
      width: this.croppedWidthPX || this.canvas.width,
      height: this.croppedHeightPX || this.canvas.height,
    };

    const croppingBoxCenterX =
      this.canvas.width / 2 - this._cropToolDimensions.width / 2;
    const croppingBoxCenterY =
      this.canvas.height / 2 - this._cropToolDimensions.height / 2;
    this._corners = {
      'top-left': {
        id: 'top-left',
        x: croppingBoxCenterX,
        y: croppingBoxCenterY,
        xBounded: 'x1',
        yBounded: 'y1',
        shapes: [
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: this._croppingToolsBaseSize * this._canvasScale,
            h: (this._croppingToolsBaseSize / 2) * this._canvasScale,
          },
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: (this._croppingToolsBaseSize / 2) * this._canvasScale,
            h: this._croppingToolsBaseSize * this._canvasScale,
          },
        ],
      },
      'top-right': {
        id: 'top-right',
        x: croppingBoxCenterX + this._cropToolDimensions.width,
        y: croppingBoxCenterY,
        xBounded: 'x2',
        yBounded: 'y1',
        shapes: [
          {
            type: 'rect',
            relativeX:
              -(this._croppingToolsBaseSize - this._croppingToolsBaseSize / 4) *
              this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: this._croppingToolsBaseSize * this._canvasScale,
            h: (this._croppingToolsBaseSize / 2) * this._canvasScale,
          },
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: (this._croppingToolsBaseSize / 2) * this._canvasScale,
            h: this._croppingToolsBaseSize * this._canvasScale,
          },
        ],
      },
      'bottom-right': {
        id: 'bottom-right',
        x: croppingBoxCenterX + this._cropToolDimensions.width,
        y: croppingBoxCenterY + this._cropToolDimensions.height,
        xBounded: 'x2',
        yBounded: 'y2',
        shapes: [
          {
            type: 'rect',
            relativeX:
              (-this._croppingToolsBaseSize + this._croppingToolsBaseSize / 4) *
              this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: this._croppingToolsBaseSize * this._canvasScale,
            h: (this._croppingToolsBaseSize / 2) * this._canvasScale,
          },
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY:
              -(this._croppingToolsBaseSize - this._croppingToolsBaseSize / 4) *
              this._canvasScale,
            w: (this._croppingToolsBaseSize / 2) * this._canvasScale,
            h: this._croppingToolsBaseSize * this._canvasScale,
          },
        ],
      },
      'bottom-left': {
        id: 'bottom-left',
        x: croppingBoxCenterX,
        y: croppingBoxCenterY + this._cropToolDimensions.height,
        xBounded: 'x1',
        yBounded: 'y2',
        shapes: [
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            w: this._croppingToolsBaseSize * this._canvasScale,
            h: (this._croppingToolsBaseSize / 2) * this._canvasScale,
          },
          {
            type: 'rect',
            relativeX: (-this._croppingToolsBaseSize / 4) * this._canvasScale,
            relativeY:
              -(this._croppingToolsBaseSize - this._croppingToolsBaseSize / 4) *
              this._canvasScale,
            w: (this._croppingToolsBaseSize / 2) * this._canvasScale,
            h: this._croppingToolsBaseSize * this._canvasScale,
          },
        ],
      },
    };
    this._framePath = new Path2D();
    this._framePath.rect(
      this._corners['top-left'].x,
      this._corners['top-left'].y,
      this._corners['top-right'].x - this._corners['top-left'].x,
      this._corners['bottom-left'].y - this._corners['top-left'].y,
    );
  }

  private _drawBackdrops(): void {
    if (!this.ctx) {
      return;
    }
    this.ctx.fillStyle = this._backdropColor;
    const radius = Math.min(
      (this._corners['top-right'].x - this._corners['top-left'].x) / 2,
      (this._corners['bottom-left'].y - this._corners['top-left'].y) / 2,
    );
    const centerX =
      this._corners['top-left'].x +
      (this._corners['top-right'].x - this._corners['top-left'].x) / 2;
    const centerY =
      this._corners['top-left'].y +
      (this._corners['bottom-left'].y - this._corners['top-left'].y) / 2;

    this.ctx.save();
    // Create a path covering the entire canvas
    this.ctx.beginPath();
    this.ctx.rect(
      this._corners['top-left'].x,
      this._corners['top-left'].y,
      this._corners['top-right'].x - this._corners['top-left'].x,
      this._corners['bottom-left'].y - this._corners['top-left'].y,
    );

    // Create a path for the circle
    this.ctx.moveTo(centerX + radius, centerY);
    this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);

    // Exclude the circular area from the rectangle
    this.ctx.closePath();
    this.ctx.clip('evenodd');

    // Fill the canvas with a color (white in this case)
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();

    const leftTopCorner = this._movableElements['top-left'];
    const rightBottomCorner = this._movableElements['bottom-right'];
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    if (leftTopCorner && rightBottomCorner) {
      this.ctx.fillRect(0, 0, leftTopCorner.x, this.canvas.height);
      this.ctx.fillRect(
        rightBottomCorner.x,
        0,
        this.canvas.width,
        this.canvas.height,
      );
      this.ctx.fillRect(
        leftTopCorner.x,
        0,
        rightBottomCorner.x - leftTopCorner.x,
        leftTopCorner.y,
      );
      this.ctx.fillRect(
        leftTopCorner.x,
        rightBottomCorner.y,
        rightBottomCorner.x - leftTopCorner.x,
        this.canvas.height,
      );
    }
  }

  private _roundToStepValue(value: number): number {
    return Math.round(value / this.imageScaleStep) * this.imageScaleStep;
  }

  private _calculateCanvasResizeScale(): void {
    this._canvasScale = this.canvas.width / this.canvas.offsetWidth;
  }

  private _calculateImageScaleToFillHorizontallyAndVerticallyCanvas(): void {
    const dimensions = this._calculateRotatedDimensions(
      this.image.width,
      this.image.height,
      this.imageRotation,
    );
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;
    const imageWidth = dimensions.width;
    const imageHeight = dimensions.height;
    const scaleToFillWidth = canvasWidth / imageWidth;
    const scaleToFillHeight = canvasHeight / imageHeight;
    this.imageScale = Math.max(scaleToFillWidth, scaleToFillHeight);
    this.imageScaleMax = Math.ceil(this.imageScale * 2 * 10) / 10;
    this.imageScaleMin = Math.ceil(this.imageScale) / 10;
    this._cdRef.detectChanges();
  }
}
